import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Clock, Loader2, Repeat, Sparkles, Baby, UserCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { FeatureShell, DemoPanel } from "../components/sections/FeatureShell";
import { API, getProfile } from "../lib/api";
import { visitorId, streamPost } from "../lib/stream";

const DIETS = ["Nut-free", "Vegetarian", "Dairy-free", "Gluten-free", "Halal", "Low sodium"];

const householdText = (members) =>
  members.map((m) => `${m.name}${m.age ? ` (${m.age})` : ""}`).join(", ").slice(0, 200);

const parseMeals = (text) =>
  text.split("\n").map((l) => { try { return JSON.parse(l.trim()); } catch { return null; } }).filter((m) => m && typeof m.name === "string");

const Field = ({ label, children }) => (<label className="block"><span className="eyebrow mb-2 block">{label}</span>{children}</label>);
const inputCls = "w-full rounded-xl border border-cyan-400/25 bg-deep/70 px-4 py-3 text-white placeholder:text-slate-400 focus:border-cyan-300 focus:outline-none";

const MealCard = ({ m, i }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid={`ai-meal-card-${i}`} className="rounded-2xl border border-cyan-400/30 bg-deep/70 p-6">
    <div className="flex items-start justify-between gap-3">
      <h3 className="font-display text-2xl font-semibold text-white">{m.name}</h3>
      <span className="flex shrink-0 items-center gap-1 rounded-full bg-cyan-400/10 px-3 py-1 text-xs text-cyan-200"><Clock className="h-3 w-3" />{m.time_minutes ?? "?"} min</span>
    </div>
    <p className="mt-2 text-sm text-slate-300">{m.summary}</p>
    <p className="eyebrow mb-2 mt-5">Why this fits</p>
    <ul className="space-y-1.5">{(Array.isArray(m.why) ? m.why : []).map((w) => <li key={w} className="flex gap-2 text-sm text-slate-200"><Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />{w}</li>)}</ul>
    {m.swap && <p className="mt-4 flex gap-2 text-sm text-slate-300"><Repeat className="mt-0.5 h-4 w-4 shrink-0 text-blue-300" /><span><span className="text-white">Swap: </span>{m.swap}</span></p>}
    {m.kid_tip && <p className="mt-2 flex gap-2 text-sm text-slate-300"><Baby className="mt-0.5 h-4 w-4 shrink-0 text-blue-300" /><span><span className="text-white">Little helper: </span>{m.kid_tip}</span></p>}
  </motion.div>
);

export default function Meals() {
  const [form, setForm] = useState({ household: "2 adults, a 7-year-old and a 4-year-old", pantry: "chicken, zucchini, rice, garlic", goal: "More vegetables on weeknights", time_minutes: 30 });
  const [dietary, setDietary] = useState(["Nut-free"]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const [usingProfile, setUsingProfile] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ai/quota`, { params: { visitor_id: visitorId() } }).then((r) => setQuota(r.data)).catch(() => setQuota(null));
    getProfile(visitorId()).then((p) => {
      if (!p) return;
      const members = Array.isArray(p.members) ? p.members : [];
      setForm((f) => ({ ...f, household: members.length ? householdText(members) : f.household, goal: p.goals?.length ? p.goals.join(", ").slice(0, 200) : f.goal }));
      setDietary((Array.isArray(p.diets) ? p.diets : []).filter((d) => DIETS.includes(d)));
      setUsingProfile(true);
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true); setText("");
    try {
      await streamPost(`${API}/ai/meals`, { ...form, time_minutes: Number(form.time_minutes), dietary, visitor_id: visitorId() }, (ev) => {
        if (ev.remaining !== undefined) setQuota((q) => ({ ...(q ?? {}), remaining: ev.remaining, model: ev.model }));
        if (ev.t) setText((t) => t + ev.t);
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const meals = parseMeals(text);
  const out = quota?.remaining === 0;

  return (
    <FeatureShell slug="meal-recommendations" cta="Try live AI meal ideas">
      <DemoPanel id="why" live title="Ask Honesti for tonight’s dinner" note={`Live AI: real ideas are generated by ChatGPT (${quota?.model ?? "gpt-5.6-terra"}). Your answers are sent to the AI only to create these ideas and are not saved to a profile. Always check ingredients for allergies.`}>
        <div className="grid gap-8 lg:grid-cols-12">
          <form onSubmit={generate} className="space-y-5 lg:col-span-5" data-testid="ai-meal-form">
            <p data-testid="profile-source" className="flex items-center gap-2 rounded-xl bg-white/5 px-4 py-3 text-sm text-slate-200">
              <UserCheck className="h-4 w-4 shrink-0 text-cyan-300" />
              {usingProfile ? <span>Pre-filled from your saved household profile. <Link to="/features/household-profile" className="text-cyan-200 hover:underline">Edit profile</Link></span>
                : <span>Sample details below. <Link to="/features/household-profile" className="text-cyan-200 hover:underline">Save your household profile</Link> to fill these in automatically.</span>}
            </p>
            <Field label="Your household"><input value={form.household} onChange={set("household")} maxLength={200} data-testid="ai-household-input" className={inputCls} /></Field>
            <div>
              <span className="eyebrow mb-2 block">Dietary needs</span>
              <div className="flex flex-wrap gap-2">
                {DIETS.map((d) => (
                  <button type="button" key={d} onClick={() => setDietary((l) => (l.includes(d) ? l.filter((x) => x !== d) : [...l, d]))} data-testid={`ai-diet-${d.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${dietary.includes(d) ? "border-cyan-300 bg-cyan-400/15 text-white" : "border-cyan-400/25 text-slate-300"}`}>{d}</button>
                ))}
              </div>
            </div>
            <Field label="Already in the pantry"><input value={form.pantry} onChange={set("pantry")} maxLength={300} data-testid="ai-pantry-input" className={inputCls} /></Field>
            <Field label="Family goal"><input value={form.goal} onChange={set("goal")} maxLength={200} data-testid="ai-goal-input" className={inputCls} /></Field>
            <Field label={`Time available — ${form.time_minutes} min`}><input type="range" min={10} max={90} step={5} value={form.time_minutes} onChange={set("time_minutes")} data-testid="ai-time-slider" className="w-full accent-cyan-400" /></Field>
            <button type="submit" disabled={loading || out} data-testid="ai-generate-btn" className="btn-primary w-full disabled:opacity-50">
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Honesti is thinking…</> : <><Sparkles className="h-4 w-4" /> Get 3 meal ideas</>}
            </button>
            <p data-testid="ai-quota" className="text-center text-xs text-slate-300">{quota ? `${quota.remaining} of ${quota.limit ?? 5} live requests left today` : "Live requests are limited per visitor each day"}</p>
          </form>
          <div className="space-y-4 lg:col-span-7" data-testid="ai-results">
            {meals.map((m, i) => <MealCard key={`${m.name}-${i}`} m={m} i={i} />)}
            {loading && meals.length < 3 && <div className="h-40 animate-pulse rounded-2xl border border-cyan-400/20 bg-cyan-400/5" />}
            {!loading && !meals.length && (
              <div className="grid h-full min-h-[300px] place-items-center rounded-2xl border border-dashed border-cyan-400/25 p-8 text-center text-slate-300">
                <div><Sparkles className="mx-auto h-8 w-8 text-cyan-300" /><p className="mt-4 max-w-sm">Tell Honesti about your family and press “Get 3 meal ideas” — each idea comes with the reasons it fits.</p></div>
              </div>
            )}
          </div>
        </div>
      </DemoPanel>
    </FeatureShell>
  );
}
